import React, { useMemo } from 'react';
import { getCountries } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en';
import 'react-phone-number-input/style.css';
import './CountrySelect.scss';

// Flag URL template from the library
const FLAG_URL = 'https://purecatamphetamine.github.io/country-flag-icons/3x2/{XX}.svg';

/**
 * Get the flag URL for a country code
 * @param {string} country - Two-letter country code (e.g., 'IL', 'US')
 * @returns {string} URL to the country flag SVG
 */
const getFlagUrl = (country) => {
    return FLAG_URL.replace('{XX}', country);
};

/**
 * Country selector with flags
 * Custom implementation showing flag icon next to country name
 *
 * @param {Object} props - Component props
 * @param {string} props.value - Selected country code (e.g., 'IL', 'US')
 * @param {Function} props.onChange - Callback when country changes, receives country code
 * @param {string} [props.label] - Label text for the select
 * @param {boolean} [props.required] - Whether the field is required
 * @param {boolean} [props.disabled] - Whether the field is disabled
 * @param {string} [props.dataTestId] - Test ID for testing
 */
export const CountrySelect = ({
    value,
    onChange,
    label,
    required,
    disabled,
    dataTestId
}) => {
    const countries = useMemo(() => getCountries(), []);

    const handleChange = (e) => {
        onChange(e.target.value);
    };

    const selectedCountryName = value ? (en[value] || value) : '';

    return (
        <div className="country-select-wrapper">
            {label && (
                <label className="country-select-label">
                    {label}
                    {required && <span className="required-asterisk">*</span>}
                </label>
            )}
            <div className="country-select-container">
                {/* Flag display */}
                {value && (
                    <div className="country-flag">
                        <img
                            src={getFlagUrl(value)}
                            alt={selectedCountryName}
                            className="country-flag-img"
                        />
                    </div>
                )}
                {/* Country name display */}
                <span className="country-name">{selectedCountryName}</span>
                {/* Arrow */}
                <span className="country-select-arrow" />
                {/* Invisible select overlay */}
                <select
                    className="country-select-native"
                    value={value || ''}
                    onChange={handleChange}
                    disabled={disabled}
                    data-testid={dataTestId}
                >
                    <option value="" disabled>
                        Select country
                    </option>
                    {countries.map((country) => (
                        <option key={country} value={country}>
                            {en[country] || country}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};
